import { useEffect, useRef } from 'react';

import { TransactionGMetrixTableDialog, TransactionGMetrixTableDialogHandle } from 'components/TransactionTableGMetrixDialog/TransactionGMetrixTableDialog';
import { GroupGMetrixTableDialog, GroupGMetrixTableDialogHandle } from 'components/GroupGMetrixTableDialog/GroupGMetrixTableDialog';
import type { ContentTiedToExpiredOrder } from 'pages/access_codes/components/TransactionProfile/TransactionProfile';
import { contentRestrictedByInactiveOrders, getMostRestrictedLicenseDeliverable } from 'utils/objects';
import { ClassroomContent } from 'pages/groups/components/ClassroomContent/ClassroomContent';
import { ClassroomDetails } from 'pages/groups/components/ClassroomDetails/ClassroomDetails';
import { MostRestrictiveLicense, Transaction, TransactionApi } from 'apis/TransactionApi';
import { XedReportsTab } from 'pages/groups/components/XedReportsTab/XedReportsTab';
import { Certificates } from 'pages/students/components/Certificates/Certificates';
import { StudentsTab } from 'pages/groups/components/StudentsTab/StudentsTab';
import { GroupData, useGroupDetails, useGroups } from 'pages/groups/context';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { GMetrixTabs, GMetrixTabsTab } from 'components/GMetrixTabs';
import { XedTab } from 'pages/groups/components/XedTab/XedTab';
import { Activity } from 'pages/groups/components/Activity';
import { useStickyTabIndex, useSuperState } from 'hooks';
import { FormattedMessage, useIntl } from 'react-intl';
import { StudentGroupApi } from 'apis/StudentGroupApi';
import { useNotification } from 'app/notifications';
import { Tab } from 'components/Tabs/Tabs';
import { Col, Row } from 'react-bootstrap';
import { useJoyride } from 'app/joyride';
import { getFormattedDate } from 'utils';
import { TransactionType } from 'types';
import { useApp } from 'app';

import 'pages/groups/components/GroupProfile/styles/group-profile.scss';

interface GroupProfileProps extends Tab<GroupData> {
    contentTiedToInactiveOrder: boolean;
    createNewTransaction: boolean;
    onDetailsItemClick: (
        dataItem: Transaction,
        newTransaction?: boolean,
        setTransactionCreationLoading?: React.Dispatch<React.SetStateAction<boolean>>
    ) => Promise<void>;
    reloadGroup: () => Promise<void>;
    setCreateNewTransaction: React.Dispatch<React.SetStateAction<boolean>>;
    setInactiveOrdersWContentOnOpenTransaction: React.Dispatch<React.SetStateAction<ContentTiedToExpiredOrder[]>>;
}

export const GroupProfile = (props: GroupProfileProps) => {

    const { contentTiedToInactiveOrder, Data, Id, onDetailsItemClick, reloadGroup, setInactiveOrdersWContentOnOpenTransaction } = props;

    const { userId } = useApp();
    const { permissions } = useGroups();
    const { formatMessage } = useIntl();
    const { openTabIndex, pageContext, setSubTabIndex, subTabIndex, update } = useGroupDetails();

    const [state, setState] = useSuperState({
        reportsTabOpened: subTabIndex[openTabIndex - 1] === 3,
        selectedTabStripIndex: 0,
        transactionCreationLoading: false
    });
    const { loadCurrentTour } = useJoyride();
    const { catchWithToast } = useNotification();

    const linkToAccessCodeDialogRef = useRef<TransactionGMetrixTableDialogHandle>(null);
    const groupDialogRef = useRef<GroupGMetrixTableDialogHandle>(null);

    // Remember the active sub-tab per classroom across refreshes.
    useStickyTabIndex({
        entityId: pageContext?.Id,
        onRestore: index => {

            const copyOfSubTabIndex = [...subTabIndex];
            copyOfSubTabIndex[openTabIndex - 1] = index;
            setSubTabIndex(copyOfSubTabIndex);
            setState({
                reportsTabOpened: state.reportsTabOpened || index === 3,
                selectedTabStripIndex: index
            });

        },
        scope: 'classroom',
        value: subTabIndex[openTabIndex - 1] ?? 0
    });

    // synchronize XED changes when moving away from tab index 4 (XED tab)
    const syncXEDChanges = async (newTabIdx: number) => {

        if (state.selectedTabStripIndex === 4 && newTabIdx !== 4) {

            if (
                pageContext &&
                pageContext.Data &&
                pageContext.Data.transactionDetails &&
                pageContext.Data.transactionDetails.HasXED === true &&
                pageContext.Data.transactionDetails.XEDExternalId &&
                pageContext.Data.transactionDetails.XEDExternalId.length > 0
            ) {

                await TransactionApi.SyncXEDGroup(pageContext.Data.transactionDetails.Id, -1, -1);

                let assignedContent = await TransactionApi.GetLimitedContentByTransactionId(
                    pageContext.Data.transactionDetails.Id,
                    pageContext.Data.groupDetails?.Partner?.Id
                ).catch(catchWithToast(formatMessage({ id: 'failed_to_load_assigned_content.3392953e', defaultMessage: 'Failed to load assigned content.' })));

                if (assignedContent && assignedContent.length > 0) {

                    assignedContent = assignedContent.map(content => {

                        if (content.ExpirationDate && content.StartDate && content.ExpirationDateString) {

                            content.ExpirationDate = new Date(content.ExpirationDate);
                            content.ExpirationDateString = getFormattedDate(new Date(content.ExpirationDateString));
                            content.StartDate = new Date(content.StartDate);
                            content.StartDateString = getFormattedDate(new Date(content.StartDateString));

                        }
                        return content;

                    });

                    update({
                        content: assignedContent,
                        contentOriginal: assignedContent
                    });

                }

            }

        }

    };

    const tabNavigationClick = (value: number) => {

        const copyOfSubTabIndex = [...subTabIndex];
        copyOfSubTabIndex[openTabIndex - 1] = value;
        setSubTabIndex(copyOfSubTabIndex);
        syncXEDChanges(value);
        setState({
            reportsTabOpened: state.reportsTabOpened || value === 3,
            selectedTabStripIndex: value
        });

    };

    useEffect(() => {

        loadCurrentTour('DetailsTab');

    }, []);

    useEffect(() => {

        if (contentTiedToInactiveOrder) {

            // Navigating to the "Content" tab for Classrooms
            //when content is tied to an expired order.
            tabNavigationClick(1);

        }

    }, [contentTiedToInactiveOrder]);

    useEffect(() => {

        if (
            pageContext &&
            pageContext.Data.inactiveOrders &&
            pageContext.Data.inactiveOrders.length > 0 &&
            pageContext.Data.content
        ) {

            const ordersAndContent = contentRestrictedByInactiveOrders(
                pageContext.Data.content,
                pageContext.Data.inactiveOrders
            );
            if (ordersAndContent.length > 0) {

                setInactiveOrdersWContentOnOpenTransaction(ordersAndContent);

            } else {

                setInactiveOrdersWContentOnOpenTransaction([]);

            }

        } else {

            setInactiveOrdersWContentOnOpenTransaction([]);

        }

    }, [pageContext?.Id, pageContext?.Data.content, pageContext?.Data.inactiveOrders]);

    useEffect(() => {

        if (pageContext?.Data.licenses && pageContext.Data.content && !pageContext.Data.mostRestrictiveLicense) {

            update({
                mostRestrictiveLicense: getMostRestrictedLicenseDeliverable(
                    pageContext.Data.licenses,
                    pageContext.Data.content,
                    pageContext?.Data.transactionDetails?.HasXED === true
                )
            });

        }

    }, [pageContext?.Data.licenses, pageContext?.Data.content, pageContext?.Data.mostRestrictiveLicense]);

    return (
        <>
            {Data.groupDetails && (
                <>
                    <TransactionGMetrixTableDialog
                        actionButtonText={formatMessage({ id: 'link_to_access_code.0d914d55', defaultMessage: 'Link to Access Code' })}
                        currentUserId={userId}
                        customDialogLabel={formatMessage({ id: 'link_to_access_code.0d914d55', defaultMessage: 'Link to Access Code' })}
                        multiSelect={false}
                        option="Add"
                        ref={linkToAccessCodeDialogRef}
                        transactionAssociation={{ associationIds: [Data.groupDetails.Id], associationName: 'Group' }}
                    />
                    <GroupGMetrixTableDialog
                        actionButtonText={formatMessage({ id: 'copy_existing_classroom.6ef8c6cb', defaultMessage: 'Copy Existing Classroom' })}
                        currentUserId={userId}
                        customDialogLabel={formatMessage({ id: 'copy_existing_classroom.6ef8c6cb', defaultMessage: 'Copy Existing Classroom' })}
                        groupAssociation={{ associationIds: [Data.groupDetails.Id], associationName: 'Group' }}
                        multiSelect={false}
                        option={'Duplicate'}
                        ref={groupDialogRef}
                        removeTheseGroups={pageContext ? [pageContext?.Id] : undefined}
                    />
                </>
            )}

            <GMetrixTabs unmountOnExit onSelect={tabNavigationClick} selected={subTabIndex[openTabIndex - 1]}>
                <GMetrixTabsTab
                    title={(
                        <div className="joyride-details-tab">
                            <FormattedMessage defaultMessage="Details" id="details.7799b923" />
                        </div>
                    )}
                >
                    <ClassroomDetails
                        contentTiedToInactiveOrder={contentTiedToInactiveOrder}
                        permissions={permissions}
                        reloadGroup={reloadGroup}
                    />
                </GMetrixTabsTab>

                <GMetrixTabsTab
                    title={(
                        <div className="joyride-content-tab">
                            <FormattedMessage defaultMessage="Content" id="content.e305279d" />
                        </div>
                    )}
                >
                    {permissions.Edit ?
                        (
                            <ClassroomContent
                                contentTiedToInactiveOrder={contentTiedToInactiveOrder}
                                permissions={permissions}
                            />
                        )
                        :
                        (
                            <Row>
                                <Col className="d-flex justify-content-center align-items-center m-4">
                                    <FormattedMessage defaultMessage="You do not have permission to edit classroom content." id="you_do_not_have_permission_to_edit_class.ede36307" />
                                </Col>
                            </Row>
                        )
                    }

                </GMetrixTabsTab>

                <GMetrixTabsTab
                    title={(
                        <div className="joyride-students-tab">
                            <FormattedMessage defaultMessage="Students" description="menu" id="students.c6d199ef" />
                        </div>
                    )}
                >
                    {pageContext?.Data.secondaryLoading ?
                        (
                            <GMetrixLoader />
                        )
                        :
                        (
                            <StudentsTab
                                contentTiedToInactiveOrder={contentTiedToInactiveOrder}
                                groupId={Id}
                                mode="Edit"
                                students={Data.studentList}
                            />
                        )
                    }
                </GMetrixTabsTab>

                <GMetrixTabsTab
                    title={(
                        <div className="joyride-activity-tab">
                            <FormattedMessage defaultMessage="Reports" id="reports.c01b78b6" />
                        </div>
                    )}
                >
                    {state.reportsTabOpened && <Activity />}
                </GMetrixTabsTab>

                {pageContext?.Data.transactionDetails?.HasXED && (
                    <GMetrixTabsTab
                        title={(
                            <div className="joyride-xed-tab">
                                <FormattedMessage defaultMessage="XED Settings" id="xed_settings.90e94a97" />
                            </div>
                        )}
                    >
                        <XedTab />
                    </GMetrixTabsTab>
                )}

                {pageContext?.Data.transactionDetails?.HasXED && (
                    <GMetrixTabsTab
                        title={(
                            <div className="joyride-xed-reports-tab">
                                <FormattedMessage defaultMessage="XED Reports" id="xed_reports.5c4e19c2" />
                            </div>
                        )}
                    >
                        <XedReportsTab />
                    </GMetrixTabsTab>
                )}
                <GMetrixTabsTab
                    title={(
                        <div className="joyride-certificates-tab">
                            <FormattedMessage defaultMessage="Certificates" id="certificates.03accec8" />
                        </div>
                    )}
                >
                    <Certificates context="classroom" id={pageContext?.Data.transactionDetails?.Id} />
                </GMetrixTabsTab>
            </GMetrixTabs>
        </>
    );

};
